import type { ContentKind, UserPreferences } from '@airpost/shared';
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly payload: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken = '';
export function setCsrfToken(t: string): void {
  csrfToken = t;
}

export async function api<T = unknown>(method: string, url: string, body?: unknown, init: { signal?: AbortSignal } = {}): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: { ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(method !== 'GET' ? { 'x-csrf-token': csrfToken } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
    signal: init.signal,
  });
  const ct = res.headers.get('content-type') ?? '';
  const data: unknown = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const d = (typeof data === 'object' && data ? data : {}) as { code?: string; error?: string };
    throw new ApiError(res.status, d.code ?? `HTTP_${res.status}`, d.error ?? res.statusText, data);
  }
  return data as T;
}

export const get = <T,>(url: string) => api<T>('GET', url);
export const post = <T,>(url: string, body?: unknown) => api<T>('POST', url, body ?? {});
export const put = <T,>(url: string, body?: unknown) => api<T>('PUT', url, body ?? {});
export const del = <T,>(url: string) => api<T>('DELETE', url);

/**
 * Bytes (a file, a piece of one) to the server as they are, with the session's CSRF token; `onProgress` hears how many
 * have gone so far. XMLHttpRequest rather than fetch: only it reports how an upload is going.
 */
export function sendBytes<T = unknown>(method: string, url: string, body: Blob, headers: Record<string, string>, opts: { onProgress?: (sent: number) => void; signal?: AbortSignal } = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (opts.signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const x = new XMLHttpRequest();
    const abort = () => x.abort();
    const done = () => opts.signal?.removeEventListener('abort', abort);
    x.open(method, url);
    for (const [k, v] of Object.entries({ ...headers, 'x-csrf-token': csrfToken })) x.setRequestHeader(k, v);
    if (opts.onProgress) x.upload.onprogress = (e) => opts.onProgress!(e.loaded);
    x.onload = () => {
      done();
      let data: unknown = x.responseText;
      try {
        data = JSON.parse(x.responseText);
      } catch {
        // not JSON: kept as text
      }
      if (x.status >= 200 && x.status < 300) return resolve(data as T);
      const d = (typeof data === 'object' && data ? data : {}) as { code?: string; error?: string };
      reject(new ApiError(x.status, d.code ?? `HTTP_${x.status}`, d.error ?? x.statusText, data));
    };
    x.onerror = () => {
      done();
      reject(new ApiError(0, 'NETWORK', 'Η σύνδεση με τον server κόπηκε', null));
    };
    x.onabort = () => {
      done();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    opts.signal?.addEventListener('abort', abort, { once: true });
    x.send(body);
  });
}

export interface SessionInfo {
  user: { id: string; username: string; displayName: string; role: string; departmentId: string; locale: string; preferences: UserPreferences };
  permissions: string[];
  csrfToken: string;
}

export interface RundownItemDto {
  id: string;
  position: number;
  storyId: string | null;
  kind: string;
  displayNumber: string | null;
  title: string;
  category: string;
  readS: number;
  readComputedS: number;
  readOverridden: boolean;
  videoS: number;
  totalS: number;
  runningS: number;
  order: number;
  floated: boolean;
  mediaAssetId: string | null;
  /** The story's virtual video code while no clip is linked. */
  mediaCode: string | null;
  /** The clip plays without its sound (ΒΙΝΤΕΟ VO): the playout puts it on its VO player. */
  mediaVo: boolean;
  /** The story's other clips, sent to the playout besides the row's own (ADR 086): each under a ΒΙΝΤΕΟ VO box or not. */
  clips: Array<{ mediaAssetId: string; vo: boolean }>;
  channel: string | null;
  storyRevision: number | null;
  /** draft | ready | approved, when the row has a story. */
  storyStatus: string | null;
  storyUpdatedAt: string;
  hasText: boolean;
  titleOverride: string | null;
  /** What the story holds (its storyline's boxes), counted: the icons after the title. */
  contents: Array<{ kind: ContentKind; n: number }>;
}

export interface RundownDto {
  id: string;
  programmeId: string;
  programmeName: string;
  date: string;
  status: 'draft' | 'linked' | 'published' | 'archived';
  revision: number;
  publishedRevision: number | null;
  targetDurationS: number | null;
  count: number;
  totalS: number;
  items: RundownItemDto[];
}

export interface StoryDto {
  id: string;
  title: string;
  bodyHtml: string;
  bodyText: string;
  wordCount: number;
  categoryId: string | null;
  originUserId: string | null;
  guideComments: string | null;
  displayNumber: string | null;
  videoS: number;
  mediaAssetId: string | null;
  mediaCode: string | null;
  readComputedS: number;
  readOverrideS: number | null;
  kind: string;
  status: string;
  /** Boxes inside boxes: box key → the second it comes in inside the box before it. */
  storylineAt?: Record<string, number>;
  revision: number;
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Programme {
  id: string;
  name: string;
  kind: string;
  startTime: string | null;
  targetDurationS: number | null;
  playoutPlayerId: number | null;
}
